import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { FeedPostDto, feedPostSummary } from '../../data-transfer-objects/feed-post.dto';
import { PersonLinkComponent } from './person-link.component';

/**
 * Posts on the Updates feed, listed so they can be judged.
 *
 * The abuse case grew this list first, because the 2026-09-07 case was two
 * posts and nothing else. The events board needed the same thing from the
 * other end: its row had always carried a "2 feed posts" count with no way to
 * see them, which is the one form in which a post cannot be judged - the line
 * reads the same whether the organizer is promoting their own space or a
 * stranger is advertising into it. Rather than copy the markup across, the
 * list lives here and both boards pass their own posts to it.
 *
 * Deleting is optional. The abuse case is where an account's posts are dealt
 * with, so it passes a handler; a board that is only explaining its own
 * number shows the posts without a button that would need its own confirm
 * flow.
 */
@Component({
  selector: 'app-feed-post-list',
  standalone: true,
  imports: [CommonModule, MatButtonModule, PersonLinkComponent],
  template: `
    <ul class="people">
      <li *ngFor="let p of posts" class="post" [class.hidden-post]="hiddenIds.includes(p.id)">
        <a
          *ngIf="p.images.length"
          class="thumb"
          [href]="p.images[0]"
          target="_blank"
          rel="noopener"
        >
          <img [src]="p.images[0]" alt="" />
        </a>
        <div class="post-body">
          <span class="title">{{ summary(p) }}</span>
          <span class="meta">
            <app-person-link
              *ngIf="p.author || showAuthor"
              [uid]="p.author?.uid ?? null"
              [name]="p.author?.name"
              fallback="account deleted"
            ></app-person-link>
            <span *ngIf="p.author || showAuthor"> · </span>
            {{ p.type }}<span *ngIf="p.eventVenue"> · {{ p.eventVenue }}</span>
            · {{ p.createdAt | date: 'd MMM yyyy, HH:mm' }}
            · seen by {{ p.viewedCount }}, liked by {{ p.likesCount }}
          </span>
          <span class="meta note" *ngIf="noteFor(p)">{{ noteFor(p) }}</span>
        </div>
        <button
          *ngIf="deletable"
          mat-stroked-button
          color="warn"
          (click)="remove.emit(p)"
          [disabled]="busy"
        >
          {{ busy ? 'Working…' : 'Delete post' }}
        </button>
      </li>
    </ul>
  `,
  styles: [
    `
      ul.people {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .post {
        display: flex;
        gap: 14px;
        align-items: center;
      }

      .post .thumb {
        flex: 0 0 auto;
      }

      .post .thumb img {
        width: 56px;
        height: 56px;
        object-fit: cover;
        border-radius: 8px;
        display: block;
        background: rgba(0, 0, 0, 0.06);
      }

      .post .post-body {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;
      }

      .post .title {
        overflow-wrap: anywhere;
        color: #12332f;
      }

      /* Both boards style .meta on their own page, and emulated encapsulation
         stops that reaching in here, so the list carries its own. */
      .post .meta {
        color: #8f8c88;
        font-size: 12.5px;
        line-height: 1.5;
      }

      /* Still counted, but no member can see it. Dimmed rather than hidden:
         the reason the count and the feed disagree is the point. */
      .post.hidden-post .thumb img,
      .post.hidden-post .title {
        opacity: 0.55;
      }

      .post .note {
        color: #a62639;
      }
    `,
  ],
})
export class FeedPostListComponent {
  @Input() posts: FeedPostDto[] = [];

  /** Whether each row offers to take the post off the feed. */
  @Input() deletable = false;

  /** Set while a delete is in flight, so the buttons stop taking clicks. */
  @Input() busy = false;

  /**
   * Show the author line even for posts that carry no author row.
   * A list that is already about one account leaves this off.
   */
  @Input() showAuthor = false;

  /** Posts members cannot see - a banned or deleted author, usually. */
  @Input() hiddenIds: string[] = [];

  /** Why a row is dimmed, by post id. */
  @Input() notes: Record<string, string> = {};

  @Output() remove = new EventEmitter<FeedPostDto>();

  summary = feedPostSummary;

  noteFor(p: FeedPostDto): string | null {
    return this.notes[p.id] ?? null;
  }
}
